import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import type { StaffSignupCode, StaffSignupCodeRecord } from '@triedge/staff-settings-domain';
import { Card, SectionHeader, Input, Button, Icon, Modal } from '../../components/ui';
import { useYstReducedMotion } from '../../../../components/motion/ystMotion';
import { SupportErrorMessage } from '../../../../components/overlays/EngineeringSupportBanner';
import {
  useCreateStaffCodeMutation,
  useListStaffCodesQuery,
  useRevokeStaffCodeMutation,
  useRotateStaffCodeMutation,
} from '../../../../state/domainRouterApi';
import { useStaffSettingsState } from '../../hooks/useStaffSettingsState';
import {
  readStaffProvisioningState,
} from '../../utils/staffProvisioning';

const REVEAL_TIMEOUT_MS = 60_000;
const MASK = '••••••••••••';

type StaffCodeRow = {
  codeId: string;
  label: string;
  maskedValue: string;
  createdAt: string;
  status: 'active' | 'revoked';
};

type CreateFormState = {
  label: string;
};

type RevealEntry = {
  codeId: string;
  fullValue: string;
  expiresAt: number;
};

type StaffCodeSource = Pick<StaffSignupCodeRecord, 'codeId' | 'label' | 'maskedValue' | 'createdAt' | 'status'>;

const toText = (value: unknown): string => String(value ?? '').trim();
const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
const asRecordArray = (value: unknown): Record<string, unknown>[] =>
  Array.isArray(value)
    ? value
        .filter((entry) => entry && typeof entry === 'object' && !Array.isArray(entry))
        .map((entry) => entry as Record<string, unknown>)
    : [];

const toStaffCodeSource = (value: unknown): StaffCodeSource | null => {
  const record = asRecord(value);
  const codeId = toText(record.codeId);
  if (!codeId) return null;
  return {
    codeId,
    label: toText(record.label) || 'Unnamed Code',
    maskedValue: toText(record.maskedValue) || MASK,
    createdAt: toText(record.createdAt),
    status: record.status === 'revoked' ? 'revoked' : 'active',
  };
};

const normalizeCodes = (items: readonly StaffSignupCode[] | undefined): StaffCodeRow[] =>
  [...(items ?? [])]
    .map((row) => ({
      codeId: toText(row.codeId),
      label: toText(row.label) || 'Unnamed Code',
      maskedValue: toText(row.maskedValue) || MASK,
      createdAt: toText(row.createdAt),
      status: row.status === 'revoked' ? ('revoked' as const) : ('active' as const),
    }))
    .sort((left, right) => right.createdAt.localeCompare(left.createdAt));

const normalizeCodeRecords = (items: readonly StaffCodeSource[] | undefined): StaffCodeRow[] =>
  [...(items ?? [])]
    .map((row) => ({
      codeId: toText(row.codeId),
      label: toText(row.label) || 'Unnamed Code',
      maskedValue: toText(row.maskedValue) || MASK,
      createdAt: toText(row.createdAt),
      status: row.status === 'revoked' ? ('revoked' as const) : ('active' as const),
    }))
    .sort((left, right) => right.createdAt.localeCompare(left.createdAt));

const formatDate = (iso: string): string => {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleDateString();
};

export default function StaffCodesSection() {
  const reducedMotion = useYstReducedMotion();
  const motionProps = {
    initial: { opacity: 0, y: reducedMotion ? 0 : 6 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: reducedMotion ? 0 : 0.18, ease: [0.16, 1, 0.3, 1] as number[] },
  };

  const { settingsState, isLoading: isSettingsStateLoading } = useStaffSettingsState();
  const staffCodesQuery = useListStaffCodesQuery();
  const [createStaffCode, createStaffCodeState] = useCreateStaffCodeMutation();
  const [rotateStaffCode, rotateStaffCodeState] = useRotateStaffCodeMutation();
  const [revokeStaffCode, revokeStaffCodeState] = useRevokeStaffCodeMutation();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateFormState>({ label: '' });
  const [revealMap, setRevealMap] = useState<Record<string, RevealEntry>>({});
  const [operationError, setOperationError] = useState<string>('');
  const [revokeTarget, setRevokeTarget] = useState<StaffCodeRow | null>(null);
  const fallbackCodes = useMemo(
    () => normalizeCodes(readStaffProvisioningState(settingsState).staffSignupCodes),
    [settingsState]
  );
  const routedCodes = useMemo(
    () => normalizeCodeRecords(
      asRecordArray(asRecord(staffCodesQuery.data).items)
        .map((entry) => toStaffCodeSource(entry))
        .filter((entry): entry is StaffCodeSource => entry !== null)
    ),
    [staffCodesQuery.data]
  );
  const [storedCodes, setStoredCodes] = useState<StaffCodeRow[]>(fallbackCodes);

  useEffect(() => {
    if (staffCodesQuery.isSuccess) {
      setStoredCodes(routedCodes);
      return;
    }
    setStoredCodes(fallbackCodes);
  }, [fallbackCodes, routedCodes, staffCodesQuery.isSuccess]);

  useEffect(() => {
    const now = Date.now();
    const nextExpiring = Object.values(revealMap).reduce<number | null>((earliest, entry) => {
      if (earliest === null || entry.expiresAt < earliest) return entry.expiresAt;
      return earliest;
    }, null);
    if (nextExpiring === null) return;
    const delay = Math.max(0, nextExpiring - now);
    const timer = setTimeout(() => {
      const current = Date.now();
      setRevealMap((previous) => {
        const next = { ...previous };
        Object.keys(next).forEach((key) => {
          if (next[key].expiresAt <= current) {
            delete next[key];
          }
        });
        return next;
      });
    }, delay + 100);
    return () => clearTimeout(timer);
  }, [revealMap]);

  const codes = storedCodes;
  const isLoading =
    !staffCodesQuery.isSuccess
    && codes.length === 0
    && (staffCodesQuery.isLoading || isSettingsStateLoading);
  const isSaving =
    createStaffCodeState.isLoading
    || rotateStaffCodeState.isLoading
    || revokeStaffCodeState.isLoading;

  const storeReveal = (codeId: string, fullValue: string) => {
    setRevealMap((previous) => ({
      ...previous,
      [codeId]: { codeId, fullValue, expiresAt: Date.now() + REVEAL_TIMEOUT_MS },
    }));
  };

  const handleCreate = async () => {
    setOperationError('');
    try {
      const created = asRecord(await createStaffCode({ label: createForm.label.trim() }).unwrap());
      const nextRow = toStaffCodeSource(created);
      if (!nextRow) {
        throw new Error('createStaffCode returned no codeId.');
      }
      setStoredCodes((previous) =>
        normalizeCodeRecords([
          nextRow,
          ...previous
            .filter((entry) => entry.codeId !== nextRow.codeId)
            .map((entry) => ({
              codeId: entry.codeId,
              label: entry.label,
              maskedValue: entry.maskedValue,
              createdAt: entry.createdAt,
              status: entry.status,
            })),
        ])
      );
      const fullValue = toText(created.fullValue);
      if (fullValue) {
        storeReveal(nextRow.codeId, fullValue);
      }
      setIsCreateModalOpen(false);
      setCreateForm({ label: '' });
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Failed to create staff code.';
      setOperationError(msg);
    }
  };

  const handleRotate = async (codeId: string) => {
    setOperationError('');
    try {
      const rotated = asRecord(await rotateStaffCode({ codeId }).unwrap());
      const nextRow = toStaffCodeSource(rotated);
      if (!nextRow) {
        throw new Error('rotateStaffCode returned no codeId.');
      }
      setStoredCodes((previous) =>
        normalizeCodeRecords(
          previous.map((entry) => (
            entry.codeId === codeId
              ? nextRow
              : {
                  codeId: entry.codeId,
                  label: entry.label,
                  maskedValue: entry.maskedValue,
                  createdAt: entry.createdAt,
                  status: entry.status,
                }
          ))
        )
      );
      const fullValue = toText(rotated.fullValue);
      if (fullValue) {
        storeReveal(codeId, fullValue);
      }
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Failed to rotate staff code.';
      setOperationError(msg);
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    setOperationError('');
    try {
      await revokeStaffCode({ codeId: revokeTarget.codeId }).unwrap();
      setStoredCodes((previous) =>
        previous.map((entry) => (
          entry.codeId === revokeTarget.codeId
            ? { ...entry, status: 'revoked' }
            : entry
        ))
      );
      setRevealMap((previous) => {
        const next = { ...previous };
        delete next[revokeTarget.codeId];
        return next;
      });
      setRevokeTarget(null);
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Failed to revoke staff code.';
      setOperationError(msg);
    }
  };

  const getDisplayValue = (row: StaffCodeRow): string => {
    const reveal = revealMap[row.codeId];
    if (reveal && reveal.expiresAt > Date.now()) {
      return reveal.fullValue;
    }
    return row.maskedValue;
  };

  const isRevealed = (codeId: string): boolean => {
    const reveal = revealMap[codeId];
    return !!reveal && reveal.expiresAt > Date.now();
  };

  return (
    <motion.div {...motionProps}>
      <Card data-testid="settings-users-staff-codes-section">
        <SectionHeader
          title="Staff Signup Codes"
          description="Generate codes staff members use to self-register. Rotate or revoke codes at any time."
          action={
            <Button
              variant="outline"
              onClick={() => {
                setOperationError('');
                setIsCreateModalOpen(true);
              }}
            >
              <Icon name="Plus" className="mr-2 h-4 w-4" />
              New Code
            </Button>
          }
        />

        {operationError ? (
          <SupportErrorMessage className="mb-4">
            {operationError}
          </SupportErrorMessage>
        ) : null}

        <div className="mb-4 rounded-lg border border-coral/30 bg-warning-background px-4 py-3 text-xs text-irongrey">
          <strong>Security notice:</strong> Full code values are shown once after creation or
          rotation, then automatically masked after 60&nbsp;seconds. Codes are one-time-use for
          signup and cannot be recovered after masking.
        </div>

        <div
          data-testid="settings-users-staff-codes-mobile-list"
          className="space-y-3 md:hidden"
        >
          {isLoading ? (
            <div className="rounded-md border border-border bg-white px-4 py-6 text-center text-sm text-text-secondary">
              Loading codes…
            </div>
          ) : codes.length === 0 ? (
            <div className="rounded-md border border-border bg-white px-4 py-6 text-center text-sm text-text-secondary">
              No staff codes created yet.
            </div>
          ) : (
            codes.map((row) => (
              <div
                key={row.codeId}
                data-testid={`settings-users-staff-code-mobile-card-${row.codeId}`}
                className={`space-y-4 rounded-md border border-border bg-white p-4 ${
                  row.status === 'revoked' ? 'opacity-60' : ''
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-wider text-text-secondary">
                      Label
                    </p>
                    <p className="mt-1 text-sm font-medium text-irongrey">{row.label}</p>
                  </div>
                  <span
                    className={`inline-flex items-center rounded px-2.5 py-0.5 text-xs font-medium ${
                      row.status === 'active'
                        ? 'bg-success-background text-jungle'
                        : 'bg-error-background text-strawberry'
                    }`}
                  >
                    {row.status === 'active' ? 'Active' : 'Revoked'}
                  </span>
                </div>

                <div>
                  <div className="mb-1 flex items-center justify-between gap-3">
                    <p className="text-xs font-semibold uppercase tracking-wider text-text-secondary">
                      Code
                    </p>
                    {row.status === 'active' ? (
                      <button
                        data-testid={`settings-users-staff-code-reveal-${row.codeId}`}
                        className="text-xs text-primary transition-colors hover:text-primary/80"
                        onClick={() => {
                          if (isRevealed(row.codeId)) {
                            setRevealMap((previous) => {
                              const next = { ...previous };
                              delete next[row.codeId];
                              return next;
                            });
                          }
                        }}
                        title={isRevealed(row.codeId) ? 'Hide' : 'Reveal (rotate to get full value)'}
                      >
                        <Icon name={isRevealed(row.codeId) ? 'EyeOff' : 'Eye'} className="h-4 w-4" />
                      </button>
                    ) : null}
                  </div>
                  <code className="block break-all rounded bg-muted px-3 py-2 text-xs font-mono text-irongrey">
                    {getDisplayValue(row)}
                  </code>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-text-secondary">
                      Created
                    </p>
                    <p className="mt-1 text-sm text-text-secondary">{formatDate(row.createdAt)}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-text-secondary">
                      Visibility
                    </p>
                    <p className="mt-1 text-sm text-text-secondary">
                      {isRevealed(row.codeId) ? 'Full value visible for 60 seconds' : 'Masked'}
                    </p>
                  </div>
                </div>

                {row.status === 'active' ? (
                  <div className="flex flex-col gap-2 border-t border-border pt-3 sm:flex-row sm:justify-end">
                    <Button
                      data-testid={`settings-users-staff-code-rotate-${row.codeId}`}
                      variant="outline"
                      className="w-full sm:w-auto"
                      disabled={isSaving}
                      onClick={() => void handleRotate(row.codeId)}
                    >
                      Rotate
                    </Button>
                    <Button
                      data-testid={`settings-users-staff-code-revoke-${row.codeId}`}
                      variant="ghost"
                      className="w-full text-strawberry hover:text-strawberry/80 sm:w-auto"
                      onClick={() => setRevokeTarget(row)}
                    >
                      Revoke
                    </Button>
                  </div>
                ) : null}
              </div>
            ))
          )}
        </div>

        <div className="hidden overflow-x-auto rounded-md border border-border md:block">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                  Label
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                  Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                  Status
                </th>
                <th className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border bg-white">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-sm text-text-secondary">
                    Loading codes…
                  </td>
                </tr>
              ) : codes.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-sm text-text-secondary">
                    No staff codes created yet.
                  </td>
                </tr>
              ) : (
                codes.map((row) => (
                  <tr key={row.codeId} className={row.status === 'revoked' ? 'opacity-50' : ''}>
                    <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-irongrey">
                      {row.label}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex items-center gap-2">
                        <code className="rounded bg-muted px-2 py-1 text-xs font-mono text-irongrey">
                          {getDisplayValue(row)}
                        </code>
                        {row.status === 'active' ? (
                          <button
                            data-testid={`settings-users-staff-code-reveal-${row.codeId}`}
                            className="text-xs text-primary transition-colors hover:text-primary/80"
                            onClick={() => {
                              if (isRevealed(row.codeId)) {
                                setRevealMap((previous) => {
                                  const next = { ...previous };
                                  delete next[row.codeId];
                                  return next;
                                });
                              }
                            }}
                            title={isRevealed(row.codeId) ? 'Hide' : 'Reveal (rotate to get full value)'}
                          >
                            <Icon name={isRevealed(row.codeId) ? 'EyeOff' : 'Eye'} className="h-4 w-4" />
                          </button>
                        ) : null}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-text-secondary">
                      {formatDate(row.createdAt)}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <span
                        className={`inline-flex items-center rounded px-2.5 py-0.5 text-xs font-medium ${
                          row.status === 'active'
                            ? 'bg-success-background text-jungle'
                            : 'bg-error-background text-strawberry'
                        }`}
                      >
                        {row.status === 'active' ? 'Active' : 'Revoked'}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-right">
                      {row.status === 'active' ? (
                        <div className="flex justify-end gap-3">
                          <button
                            data-testid={`settings-users-staff-code-rotate-${row.codeId}`}
                            className="text-xs font-medium text-primary transition-colors hover:text-primary/80"
                            disabled={isSaving}
                            onClick={() => void handleRotate(row.codeId)}
                          >
                            Rotate
                          </button>
                          <button
                            data-testid={`settings-users-staff-code-revoke-${row.codeId}`}
                            className="text-xs font-medium text-strawberry transition-colors hover:text-strawberry/80"
                            onClick={() => setRevokeTarget(row)}
                          >
                            Revoke
                          </button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Modal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          title="Create Staff Signup Code"
        >
          <div className="space-y-4">
            <Input
              label="Label"
              value={createForm.label}
              onChange={(event) => setCreateForm({ label: event.target.value })}
              placeholder="e.g. Boys Division 2025–2026"
            />
            <p className="text-xs text-text-secondary">
              The full code value will be displayed once after creation. Copy it immediately.
            </p>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="ghost" onClick={() => setIsCreateModalOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => void handleCreate()}
                disabled={isSaving || !createForm.label.trim()}
              >
                {isSaving ? 'Creating…' : 'Create Code'}
              </Button>
            </div>
          </div>
        </Modal>

        <Modal
          isOpen={!!revokeTarget}
          onClose={() => setRevokeTarget(null)}
          title="Revoke Staff Code"
        >
          {revokeTarget ? (
            <div className="space-y-4">
              <p className="text-sm text-irongrey">
                Revoking <strong>{revokeTarget.label}</strong> will immediately invalidate the
                code. Any staff member who has not yet completed signup will be unable to use it.
              </p>
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="ghost" onClick={() => setRevokeTarget(null)}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={() => void handleRevoke()}
                  disabled={isSaving}
                  className="!bg-strawberry !border-transparent hover:!bg-strawberry/90"
                >
                  {isSaving ? 'Revoking…' : 'Revoke Code'}
                </Button>
              </div>
            </div>
          ) : null}
        </Modal>
      </Card>
    </motion.div>
  );
}
